import { Array } from 'effect';
import * as AsyncResult from 'effect/unstable/reactivity/AsyncResult';
import { appPath } from '../../../shared/router/appPath';
import { useIntl } from 'react-intl';

import { Button } from '@vs-shop/ui/button';
import {
  SearchPanelActions,
  SearchPanelColumn,
  SearchPanelColumnVariant,
  SearchPanelNotice,
} from '@vs-shop/commerce-ui/searchPanel';

import { Url } from '../../../route/router';

import Link from '@vs-shop/image-link/Link';
import SuggestLinkGroup from './SuggestLinkGroup';
import SuggestProductGroup from './SuggestProductGroup';
import SuggestTopProduct from './SuggestTopProduct';
import { SearchSuggestSkeleton } from '../../server/search/searchSkeletons';
import type { SuggestResult } from '../../../effect/schema/openapi/backend';

type Props = {
  result: AsyncResult.AsyncResult<SuggestResult, unknown>;
  query: string;
  onSelectAction: () => void;
};

/**
 * Presentational suggest-results renderer, split from the atom-reading wrapper so
 * the skeleton / error / empty / populated branches are testable with constructed
 * `AsyncResult` values (no live suggest request needed).
 */
export function SearchSuggestResultsView({ result, query, onSelectAction }: Props) {
  const intl = useIntl();

  // While a query is in flight (first load or a changed `q`), show a skeleton that
  // mirrors the default-panel layout so the popover keeps its size and signals
  // "something is happening" instead of collapsing to the close button. Cached
  // re-opens (same `q`, kept alive) land on a settled `Success` and skip this.
  if (result.waiting || AsyncResult.isInitial(result)) {
    return <SearchSuggestSkeleton />;
  }

  // A failed suggest request is NOT "no results" — surface a distinct error so a
  // broken/5xx backend doesn't masquerade as an empty match.
  if (AsyncResult.isFailure(result)) {
    return (
      <div className="lap:col-span-4">
        <SearchPanelNotice>
          {intl.formatMessage({
            id: 'component.client.navigation.header.searchPopover.suggest.error',
            defaultMessage: 'Vyhledávání se nezdařilo. Zkuste to prosím znovu.',
          })}
        </SearchPanelNotice>
      </div>
    );
  }

  // Only `Success` remains; the empty arrays are an unreachable type-safety fallback.
  const hits = AsyncResult.isSuccess(result) ? result.value : undefined;

  const products = hits?.products ?? [];
  const brands = hits?.brands ?? [];
  const taxons = hits?.taxons ?? [];
  const articles = hits?.articles ?? [];

  const isEmpty = [products, brands, taxons, articles].every(Array.isReadonlyArrayEmpty);

  if (isEmpty) {
    return (
      <div className="lap:col-span-4">
        <SearchPanelNotice>
          {intl.formatMessage(
            {
              id: 'component.client.navigation.header.searchPopover.suggest.empty',
              defaultMessage: 'Žádné výsledky pro „{query}“',
            },
            { query }
          )}
        </SearchPanelNotice>
      </div>
    );
  }

  // Follow the default-panel column pattern: col 1 = all non-product groups,
  // col 2 = the first product as "Top produkt", cols 3+4 = the remaining
  // products plus the full-search CTA. `SearchPanelColumn` renders the rule
  // after each one, so the grid's `gap-6` + `divide-x` separate 1│2 and 2│3+4
  // identically.
  const [topProduct, ...restProducts] = products;

  return (
    <>
      {/* Col 1 — non-products (brands / taxons / articles) */}
      <SearchPanelColumn variant={SearchPanelColumnVariant.GROUPED} className="lap:col-span-1">
        <SuggestLinkGroup
          hits={[...brands]}
          heading={intl.formatMessage({
            id: 'component.client.navigation.header.searchPopover.suggest.brands',
            defaultMessage: 'Značky',
          })}
          onSelect={onSelectAction}
        />
        <SuggestLinkGroup
          hits={[...taxons]}
          heading={intl.formatMessage({
            id: 'component.client.navigation.header.searchPopover.suggest.taxons',
            defaultMessage: 'Kategorie',
          })}
          onSelect={onSelectAction}
        />
        <SuggestLinkGroup
          hits={[...articles]}
          heading={intl.formatMessage({
            id: 'component.client.navigation.header.searchPopover.suggest.articles',
            defaultMessage: 'Články',
          })}
          onSelect={onSelectAction}
        />
      </SearchPanelColumn>

      {/* Col 2 — top product (first hit) */}
      <SearchPanelColumn variant={SearchPanelColumnVariant.FLUSH} className="lap:col-span-1">
        <SuggestTopProduct
          hit={topProduct}
          heading={intl.formatMessage({
            id: 'component.client.navigation.header.searchPopover.topProduct',
            defaultMessage: 'Top produkt',
          })}
          onSelect={onSelectAction}
        />
      </SearchPanelColumn>

      {/* Cols 3+4 — remaining products + full-search CTA */}
      <SearchPanelColumn variant={SearchPanelColumnVariant.FLUSH} className="lap:col-span-2">
        <SuggestProductGroup
          hits={restProducts}
          heading={intl.formatMessage({
            id: 'component.client.navigation.header.searchPopover.suggest.products',
            defaultMessage: 'Produkty',
          })}
          onSelect={onSelectAction}
        />
        <SearchPanelActions>
          <Button
            render={<Link href={appPath({ route: Url.search, searchParams: { q: query } })} />}
            onClick={onSelectAction}
            variant="primary"
            size="sm"
          >
            {intl.formatMessage({
              id: 'component.client.navigation.header.searchPopover.suggest.allResults',
              defaultMessage: 'ZOBRAZIT VŠE',
            })}
          </Button>
        </SearchPanelActions>
      </SearchPanelColumn>
    </>
  );
}
